/**
 * Marketing Analytics Tracker
 * Tracks user behavior for marketing insights without overwhelming data collection
 * Stores data locally and aggregates for meaningful insights
 */

import { saveAnalyticsSession } from '../firebase';

class AnalyticsTracker {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = Date.now();
    this.events = [];
    this.timers = new Map();
    this.componentViewTimes = new Map();
    this.formFieldTimes = new Map();

    // Initialize session data
    this.initializeSession();
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  initializeSession() {
    const trafficSource = this.getTrafficSource();

    const sessionData = {
      sessionId: this.sessionId,
      startTime: this.sessionStartTime,
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      windowSize: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      referrer: document.referrer || 'direct',
      trafficSource: trafficSource,
      events: []
    };

    this.saveToLocalStorage('currentSession', sessionData);
  }

  // Traffic source detection
  getTrafficSource() {
    // Check if we already detected traffic source in this session
    const sessionStorage = window.sessionStorage;
    const cachedSource = sessionStorage.getItem('traffic_source_detected');
    if (cachedSource) {
      try {
        return JSON.parse(cachedSource);
      } catch (e) {
        // If parsing fails, continue with fresh detection
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const referrer = document.referrer;
    const userAgent = navigator.userAgent.toLowerCase();

    // UTM Parameters
    const utm_source = urlParams.get('utm_source');
    const utm_medium = urlParams.get('utm_medium');
    const utm_campaign = urlParams.get('utm_campaign');
    const utm_term = urlParams.get('utm_term');
    const utm_content = urlParams.get('utm_content');

    // Custom parameters for social media
    const fbclid = urlParams.get('fbclid'); // Facebook Click ID
    const gclid = urlParams.get('gclid');   // Google Click ID
    const ref = urlParams.get('ref');       // Custom referrer

    let source = 'direct';
    let medium = 'none';
    let campaign = 'none';

    // Determine source from UTM parameters
    if (utm_source) {
      source = utm_source;
      medium = utm_medium || 'none';
      campaign = utm_campaign || 'none';
    }
    // Determine source from click IDs
    else if (fbclid) {
      source = 'facebook';
      medium = 'social';
      campaign = utm_campaign || 'facebook_ads';
    }
    else if (gclid) {
      source = 'google';
      medium = 'cpc';
      campaign = utm_campaign || 'google_ads';
    }
    // Determine source from custom ref parameter
    else if (ref) {
      source = ref;
      medium = 'referral';
      campaign = utm_campaign || 'custom_ref';
    }
    // Determine source from referrer
    else if (referrer) {
      source = this.categorizeReferrer(referrer);
      medium = this.getReferrerMedium(referrer);
    }
    // Fallback: User Agent detection for mobile apps
    else {
      const detectedSource = this.detectSourceFromUserAgent(userAgent);
      if (detectedSource) {
        source = detectedSource.source;
        medium = detectedSource.medium;
        campaign = detectedSource.campaign;
      }
    }

    const trafficSource = {
      source,
      medium,
      campaign,
      term: utm_term || null,
      content: utm_content || null,
      referrer: referrer || null,
      landingPage: window.location.pathname + window.location.search,
      timestamp: Date.now()
    };

    // Cache the detected source for this session
    try {
      sessionStorage.setItem('traffic_source_detected', JSON.stringify(trafficSource));
    } catch (e) {
      // If sessionStorage fails, continue without caching
    }

    return trafficSource;
  }

  categorizeReferrer(referrer) {
    const hostname = new URL(referrer).hostname.toLowerCase();

    // Social Media Platforms
    if (hostname.includes('tiktok.com')) return 'tiktok';
    if (hostname.includes('instagram.com')) return 'instagram';
    if (hostname.includes('facebook.com')) return 'facebook';
    if (hostname.includes('whatsapp.com') || hostname.includes('wa.me')) return 'whatsapp';
    if (hostname.includes('linkedin.com')) return 'linkedin';
    if (hostname.includes('twitter.com') || hostname.includes('x.com')) return 'twitter';
    if (hostname.includes('youtube.com')) return 'youtube';
    if (hostname.includes('telegram.org') || hostname.includes('t.me')) return 'telegram';

    // Search Engines
    if (hostname.includes('google.')) return 'google';
    if (hostname.includes('bing.com')) return 'bing';
    if (hostname.includes('yahoo.com')) return 'yahoo';
    if (hostname.includes('duckduckgo.com')) return 'duckduckgo';

    // Education/University sites
    if (hostname.includes('.edu') || hostname.includes('university') || hostname.includes('kampus')) return 'educational';

    // Default to hostname
    return hostname;
  }

  getReferrerMedium(referrer) {
    const hostname = new URL(referrer).hostname.toLowerCase();

    // Social Media
    if (['tiktok.com', 'instagram.com', 'facebook.com', 'linkedin.com', 'twitter.com', 'x.com', 'youtube.com'].some(social => hostname.includes(social))) {
      return 'social';
    }

    // Messaging Apps
    if (['whatsapp.com', 'wa.me', 'telegram.org', 't.me'].some(msg => hostname.includes(msg))) {
      return 'messaging';
    }

    // Search Engines
    if (['google.', 'bing.com', 'yahoo.com', 'duckduckgo.com'].some(search => hostname.includes(search))) {
      return 'organic';
    }

    // Educational
    if (hostname.includes('.edu') || hostname.includes('university') || hostname.includes('kampus')) {
      return 'educational';
    }

    return 'referral';
  }

  // Advanced User Agent detection for mobile apps and in-app browsers
  detectSourceFromUserAgent(userAgent) {
    // WhatsApp detection
    if (userAgent.includes('whatsapp')) {
      return {
        source: 'whatsapp',
        medium: 'messaging',
        campaign: 'whatsapp_app'
      };
    }

    // Instagram detection
    if (userAgent.includes('instagram')) {
      return {
        source: 'instagram',
        medium: 'social',
        campaign: 'instagram_app'
      };
    }

    // Facebook detection (including in-app browser)
    if (userAgent.includes('fbav') || userAgent.includes('fban') || userAgent.includes('facebook')) {
      return {
        source: 'facebook',
        medium: 'social',
        campaign: 'facebook_app'
      };
    }

    // TikTok detection
    if (userAgent.includes('tiktok') || userAgent.includes('bytedance')) {
      return {
        source: 'tiktok',
        medium: 'social',
        campaign: 'tiktok_app'
      };
    }

    // Twitter/X detection
    if (userAgent.includes('twitter') || userAgent.includes('x.com')) {
      return {
        source: 'twitter',
        medium: 'social',
        campaign: 'twitter_app'
      };
    }

    // LinkedIn detection
    if (userAgent.includes('linkedin')) {
      return {
        source: 'linkedin',
        medium: 'social',
        campaign: 'linkedin_app'
      };
    }

    // Telegram detection
    if (userAgent.includes('telegram')) {
      return {
        source: 'telegram',
        medium: 'messaging',
        campaign: 'telegram_app'
      };
    }

    // YouTube detection
    if (userAgent.includes('youtube')) {
      return {
        source: 'youtube',
        medium: 'social',
        campaign: 'youtube_app'
      };
    }

    // Mobile browser detection for general social media
    if (userAgent.includes('mobile') || userAgent.includes('android') || userAgent.includes('iphone')) {
      // Check if it's a WebView (common for social apps)
      if (userAgent.includes('webview') || (userAgent.includes('version/') && !userAgent.includes('chrome'))) {
        return {
          source: 'mobile_app',
          medium: 'webview',
          campaign: 'mobile_webview'
        };
      }
    }

    return null; // No specific source detected
  }

  // Event tracking methods
  trackEvent(eventType, data = {}) {
    const timestamp = Date.now();
    const event = {
      eventType,
      timestamp,
      sessionTime: timestamp - this.sessionStartTime,
      data,
      sessionId: this.sessionId
    };

    this.events.push(event);
    this.saveEventToSession(event);
  }

  // Component visibility tracking
  trackComponentView(componentName, isVisible) {
    const timestamp = Date.now();

    if (isVisible) {
      this.timers.set(`component_${componentName}`, timestamp);
      this.trackEvent('component_view_start', { componentName });
    } else {
      const startTime = this.timers.get(`component_${componentName}`);
      if (startTime) {
        const viewTime = timestamp - startTime;
        this.componentViewTimes.set(componentName,
          (this.componentViewTimes.get(componentName) || 0) + viewTime
        );

        this.trackEvent('component_view_end', {
          componentName,
          viewDuration: viewTime
        });

        this.timers.delete(`component_${componentName}`);
      }
    }
  }

  // Form interaction tracking
  trackFormFieldFocus(fieldName) {
    const timestamp = Date.now();
    this.timers.set(`field_${fieldName}`, timestamp);
    this.trackEvent('form_field_focus', { fieldName });
  }

  trackFormFieldBlur(fieldName, value = '') {
    const startTime = this.timers.get(`field_${fieldName}`);
    if (startTime) {
      const focusTime = Date.now() - startTime;
      this.formFieldTimes.set(fieldName, focusTime);

      this.trackEvent('form_field_blur', {
        fieldName,
        focusTime,
        hasValue: value.length > 0
      });

      this.timers.delete(`field_${fieldName}`);
    }
  }

  trackFormSubmission(formData, success = true) {
    const totalFormTime = this.getTotalFormTime();

    this.trackEvent('form_submission', {
      success,
      totalFormTime,
      fieldCount: Object.keys(formData).length,
      filledFields: Object.values(formData).filter(v => v && v.length > 0).length
    });
  }

  // Button and interaction tracking
  trackButtonClick(buttonName, context = {}) {
    this.trackEvent('button_click', {
      buttonName,
      ...context
    });
  }

  trackStepChange(fromStep, toStep) {
    const sessionTime = Date.now() - this.sessionStartTime;

    this.trackEvent('step_change', {
      fromStep,
      toStep,
      sessionTime,
      direction: this.getStepDirection(fromStep, toStep)
    });
  }

  // Calendar interaction tracking
  trackCalendarInteraction(action, data = {}) {
    this.trackEvent('calendar_interaction', {
      action, // 'open', 'date_select', 'time_select', 'close'
      ...data
    });
  }

  // University slider tracking
  trackUniversitySlider(action, slideIndex, universityName) {
    this.trackEvent('university_slider', {
      action, // 'next', 'prev', 'auto'
      slideIndex,
      universityName
    });
  }

  // Testimonial interaction
  trackTestimonialChange(testimonialIndex, action) {
    this.trackEvent('testimonial_interaction', {
      testimonialIndex,
      action // 'auto', 'manual'
    });
  }

  // Utility methods
  getTotalFormTime() {
    return Array.from(this.formFieldTimes.values()).reduce((sum, time) => sum + time, 0);
  }

  getStepDirection(fromStep, toStep) {
    const stepOrder = ['form', 'calendar', 'success'];
    const fromIndex = stepOrder.indexOf(fromStep);
    const toIndex = stepOrder.indexOf(toStep);

    if (fromIndex < toIndex) return 'forward';
    if (fromIndex > toIndex) return 'backward';
    return 'same';
  }

  // Data persistence
  saveEventToSession(event) {
    const currentSession = this.getFromLocalStorage('currentSession');
    if (currentSession) {
      currentSession.events.push(event);
      this.saveToLocalStorage('currentSession', currentSession);
    }
  }

  saveToLocalStorage(key, data) {
    try {
      localStorage.setItem(`analytics_${key}`, JSON.stringify(data));
    } catch (error) {
      console.warn('Analytics: Could not save to localStorage', error);
    }
  }

  getFromLocalStorage(key) {
    try {
      const data = localStorage.getItem(`analytics_${key}`);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.warn('Analytics: Could not read from localStorage', error);
      return null;
    }
  }

  // Session completion and storage
  async completeSession(outcome = 'unknown') {
    const sessionData = this.getFromLocalStorage('currentSession');
    if (!sessionData) return;

    const completedSession = {
      ...sessionData,
      endTime: Date.now(),
      totalDuration: Date.now() - this.sessionStartTime,
      outcome, // 'appointment_booked', 'form_abandoned', 'calendar_abandoned'
      componentViewTimes: Object.fromEntries(this.componentViewTimes),
      formFieldTimes: Object.fromEntries(this.formFieldTimes),
      totalEvents: this.events.length
    };

    // Store completed session locally
    this.storeCompletedSession(completedSession);

    // Also save to Firestore
    try {
      await saveAnalyticsSession(completedSession);
      console.log('Analytics session saved to Firestore successfully');
    } catch (error) {
      console.warn('Failed to save analytics session to Firestore:', error);
    }

    // Clear current session
    localStorage.removeItem('analytics_currentSession');
  }

  // Synchronous session completion for beforeunload
  completeSessionSync(outcome = 'unknown') {
    const sessionData = this.getFromLocalStorage('currentSession');
    if (!sessionData) return;

    const completedSession = {
      ...sessionData,
      endTime: Date.now(),
      totalDuration: Date.now() - this.sessionStartTime,
      outcome,
      componentViewTimes: Object.fromEntries(this.componentViewTimes),
      formFieldTimes: Object.fromEntries(this.formFieldTimes),
      totalEvents: this.events.length
    };

    // Store completed session locally only (sync)
    this.storeCompletedSession(completedSession);

    // Clear current session
    localStorage.removeItem('analytics_currentSession');
  }

  storeCompletedSession(sessionData) {
    try {
      const existingSessions = this.getFromLocalStorage('completedSessions') || [];
      existingSessions.push(sessionData);

      // Keep only last 100 sessions to prevent storage bloat
      if (existingSessions.length > 100) {
        existingSessions.splice(0, existingSessions.length - 100);
      }

      this.saveToLocalStorage('completedSessions', existingSessions);
    } catch (error) {
      console.warn('Analytics: Could not store completed session', error);
    }
  }

  // Analytics retrieval for admin panel
  getAnalytics() {
    const completedSessions = this.getFromLocalStorage('completedSessions') || [];
    const currentSession = this.getFromLocalStorage('currentSession');

    return {
      totalSessions: completedSessions.length,
      currentSession: currentSession,
      completedSessions: completedSessions,
      aggregatedData: this.aggregateSessionData(completedSessions)
    };
  }

  aggregateSessionData(sessions) {
    if (sessions.length === 0) return null;

    const aggregated = {
      averageSessionDuration: 0,
      totalSessions: sessions.length,
      conversionRate: 0,
      mostEngagedComponents: {},
      averageFormTime: 0,
      formAbandonmentPoints: {},
      popularFormFields: {},
      stepProgression: {
        formToCalendar: 0,
        calendarToSuccess: 0,
        totalDropoffs: 0
      },
      deviceInfo: {
        screenResolutions: {},
        languages: {},
        browsers: {}
      },
      timeMetrics: {
        averageTimeToFirstInput: 0,
        averageTimeToSubmit: 0,
        fastestCompletion: Number.MAX_SAFE_INTEGER,
        slowestCompletion: 0
      }
    };

    // Calculate averages and aggregations
    let totalDuration = 0;
    let completedAppointments = 0;
    let totalFormTime = 0;
    let formTimeSessions = 0;

    sessions.forEach(session => {
      totalDuration += session.totalDuration || 0;

      if (session.outcome === 'appointment_booked') {
        completedAppointments++;
      }

      // Component engagement
      if (session.componentViewTimes) {
        Object.entries(session.componentViewTimes).forEach(([component, time]) => {
          aggregated.mostEngagedComponents[component] =
            (aggregated.mostEngagedComponents[component] || 0) + time;
        });
      }

      // Form metrics
      if (session.formFieldTimes) {
        const sessionFormTime = Object.values(session.formFieldTimes).reduce((sum, time) => sum + time, 0);
        if (sessionFormTime > 0) {
          totalFormTime += sessionFormTime;
          formTimeSessions++;

          aggregated.timeMetrics.fastestCompletion = Math.min(
            aggregated.timeMetrics.fastestCompletion,
            sessionFormTime
          );
          aggregated.timeMetrics.slowestCompletion = Math.max(
            aggregated.timeMetrics.slowestCompletion,
            sessionFormTime
          );
        }
      }

      // Device info
      if (session.screenResolution) {
        aggregated.deviceInfo.screenResolutions[session.screenResolution] =
          (aggregated.deviceInfo.screenResolutions[session.screenResolution] || 0) + 1;
      }

      if (session.language) {
        aggregated.deviceInfo.languages[session.language] =
          (aggregated.deviceInfo.languages[session.language] || 0) + 1;
      }

      // Step progression analysis
      const events = session.events || [];
      const stepChanges = events.filter(e => e.eventType === 'step_change');

      stepChanges.forEach(event => {
        if (event.data.fromStep === 'form' && event.data.toStep === 'calendar') {
          aggregated.stepProgression.formToCalendar++;
        }
        if (event.data.fromStep === 'calendar' && event.data.toStep === 'success') {
          aggregated.stepProgression.calendarToSuccess++;
        }
      });
    });

    // Calculate final averages
    aggregated.averageSessionDuration = Math.round(totalDuration / sessions.length);
    aggregated.conversionRate = Math.round((completedAppointments / sessions.length) * 100);
    aggregated.averageFormTime = formTimeSessions > 0 ? Math.round(totalFormTime / formTimeSessions) : 0;

    // Convert component engagement to averages
    Object.keys(aggregated.mostEngagedComponents).forEach(component => {
      aggregated.mostEngagedComponents[component] = Math.round(
        aggregated.mostEngagedComponents[component] / sessions.length
      );
    });

    // Fix fastest completion if no data
    if (aggregated.timeMetrics.fastestCompletion === Number.MAX_SAFE_INTEGER) {
      aggregated.timeMetrics.fastestCompletion = 0;
    }

    return aggregated;
  }

  // Clear all analytics data (for admin use)
  clearAllData() {
    localStorage.removeItem('analytics_currentSession');
    localStorage.removeItem('analytics_completedSessions');
    this.events = [];
    this.timers.clear();
    this.componentViewTimes.clear();
    this.formFieldTimes.clear();
  }
}

// Create singleton instance
const analyticsTracker = new AnalyticsTracker();

// Auto-complete session on page unload
window.addEventListener('beforeunload', () => {
  const currentStep = window.location.hash;
  let outcome = 'session_abandoned';

  if (currentStep.includes('success')) {
    outcome = 'appointment_booked';
  } else if (currentStep.includes('calendar')) {
    outcome = 'calendar_abandoned';
  } else {
    outcome = 'form_abandoned';
  }

  // Complete session synchronously for beforeunload
  analyticsTracker.completeSessionSync(outcome);
});

// Also try to save session when visibility changes (more reliable than beforeunload)
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    const currentStep = window.location.hash;
    let outcome = 'session_paused';

    if (currentStep.includes('success')) {
      outcome = 'appointment_booked';
    } else if (currentStep.includes('calendar')) {
      outcome = 'calendar_abandoned';
    } else {
      outcome = 'form_abandoned';
    }

    // Try async save on visibility change
    analyticsTracker.completeSession(outcome);
  }
});

export default analyticsTracker;